import {
  BoxGeometry,
  BufferGeometry,
  CylinderGeometry,
  Group,
  InstancedMesh,
  type Material,
  Matrix4,
  SphereGeometry,
  TorusGeometry,
} from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

import { STREET_FURNITURE_RENDER_CONFIG as CONFIG } from
  "../../config/streetFurniture.ts";
import type {
  RuntimeStreetFurniture,
  RuntimeStreetFurnitureKind,
  RuntimeWorldTilePayloadV2,
} from "../../world/schema/runtimeTile.ts";
import type { TerrainTileData } from "../../world/schema/tile.ts";
import { sampleTerrainTileData } from "../../world/terrain.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";

export interface StreetFurnitureBatch {
  readonly object: Group;
  readonly instanceCount: number;
  readonly countsByKind: Readonly<Partial<Record<RuntimeStreetFurnitureKind, number>>>;
}

interface Placement {
  readonly source: Readonly<RuntimeStreetFurniture>;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly yaw: number;
}

interface FurniturePart {
  readonly name: string;
  readonly geometry: BufferGeometry;
  readonly material: Material;
}

export function createStreetFurnitureBatch(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  terrain: Readonly<TerrainTileData>,
  materials: CityMaterialLibrary,
): StreetFurnitureBatch | null {
  const placements = (tile.streetFurniture ?? []).flatMap((source) => {
    const y = sampleTerrainTileData(terrain, { x: 0, z: 0 }, source.position);
    return y === null ? [] : [{
      source,
      x: source.position.x,
      y: y + CONFIG.surfaceLiftM,
      z: source.position.z,
      yaw: (source.directionDegrees ?? stableYaw(source.id)) * Math.PI / 180,
    }];
  });
  if (placements.length === 0) return null;
  const groups = new Map<RuntimeStreetFurnitureKind, Placement[]>();
  for (const placement of placements) {
    const values = groups.get(placement.source.kind);
    if (values === undefined) groups.set(placement.source.kind, [placement]);
    else values.push(placement);
  }
  const object = new Group();
  object.name = "runtime-street-furniture:" + tile.id;
  object.userData = {
    tileId: tile.id,
    sourceLayer: "openstreetmap-exact-street-furniture-nodes",
    sourceLicense: "ODbL-1.0",
    placement: "exact-osm-node-no-procedural-furniture-inference",
  };
  const matrix = new Matrix4();
  for (const [kind, values] of groups) {
    for (const part of furnitureParts(kind, materials)) {
      const mesh = new InstancedMesh(
        part.geometry,
        part.material,
        values.length,
      );
      mesh.name = object.name + ":" + kind + ":" + part.name;
      mesh.castShadow = kind !== "manhole" && kind !== "drain";
      mesh.receiveShadow = true;
      for (const [index, placement] of values.entries()) {
        matrix.makeRotationY(placement.yaw);
        matrix.setPosition(placement.x, placement.y, placement.z);
        mesh.setMatrixAt(index, matrix);
      }
      mesh.instanceMatrix.needsUpdate = true;
      object.add(mesh);
    }
  }
  return {
    object,
    instanceCount: placements.length,
    countsByKind: Object.fromEntries(
      [...groups].map(([kind, values]) => [kind, values.length]),
    ),
  };
}

function furnitureParts(
  kind: RuntimeStreetFurnitureKind,
  materials: CityMaterialLibrary,
): readonly FurniturePart[] {
  if (kind === "bollard") {
    return [{
      name: "body",
      geometry: translated(
        new CylinderGeometry(
          CONFIG.bollardRadiusM,
          CONFIG.bollardRadiusM * 1.08,
          CONFIG.bollardHeightM,
          8,
        ),
        0,
        CONFIG.bollardHeightM / 2,
        0,
      ),
      material: materials.streetPole,
    }, {
      name: "reflector",
      geometry: translated(
        new CylinderGeometry(
          CONFIG.bollardRadiusM * 1.04,
          CONFIG.bollardRadiusM * 1.04,
          0.08,
          8,
        ),
        0,
        CONFIG.bollardHeightM * 0.72,
        0,
      ),
      material: materials.roadSignIvory,
    }];
  }
  if (kind === "waste_basket") {
    return [{
      name: "basket-and-post",
      geometry: mergeOwned([
        translated(
          new CylinderGeometry(0.045, 0.05, CONFIG.wasteBasketHeightM, 8),
          0,
          CONFIG.wasteBasketHeightM / 2,
          0,
        ),
        translated(
          new BoxGeometry(
            CONFIG.wasteBasketWidthM,
            0.54,
            CONFIG.wasteBasketWidthM * 0.72,
          ),
          0,
          0.66,
          0,
        ),
      ], "street-furniture:waste-basket"),
      material: materials.streetPole,
    }];
  }
  if (kind === "bicycle_parking") {
    const rack = new TorusGeometry(
      CONFIG.bicycleRackRadiusM,
      CONFIG.bicycleRackTubeRadiusM,
      6,
      14,
    );
    rack.rotateY(Math.PI / 2);
    rack.translate(0, CONFIG.bicycleRackRadiusM, 0);
    return [{
      name: "inverted-u-rack",
      geometry: rack,
      material: materials.roadSignBack,
    }];
  }
  if (kind === "street_cabinet") {
    return [{
      name: "cabinet",
      geometry: translated(
        new BoxGeometry(
          CONFIG.cabinetWidthM,
          CONFIG.cabinetHeightM,
          CONFIG.cabinetDepthM,
        ),
        0,
        CONFIG.cabinetHeightM / 2,
        0,
      ),
      material: materials.roadSignBack,
    }];
  }
  if (kind === "manhole") {
    return [{
      name: "cover",
      geometry: translated(
        new CylinderGeometry(
          CONFIG.manholeRadiusM,
          CONFIG.manholeRadiusM,
          0.025,
          16,
        ),
        0,
        0.0125,
        0,
      ),
      material: materials.roadSignBack,
    }];
  }
  if (kind === "drain") {
    const bars = Array.from({ length: 7 }, (_, index) =>
      translated(
        new BoxGeometry(
          CONFIG.drainBarWidthM,
          0.012,
          CONFIG.drainDepthM * 0.78,
        ),
        (index - 3) * CONFIG.drainBarWidthM * 1.55,
        0.025,
        0,
      )
    );
    return [{
      name: "storm-drain-frame",
      geometry: translated(
        new BoxGeometry(CONFIG.drainWidthM, 0.025, CONFIG.drainDepthM),
        0,
        0.0125,
        0,
      ),
      material: materials.roadSignBack,
    }, {
      name: "storm-drain-grate-bars",
      geometry: mergeOwned(bars, "street-furniture:storm-drain-grate"),
      material: materials.streetPole,
    }];
  }
  if (kind === "bench") {
    return [{
      name: "bench-frame",
      geometry: mergeOwned([
        translated(new BoxGeometry(1.45, 0.12, 0.42), 0, 0.54, 0),
        translated(new BoxGeometry(1.45, 0.42, 0.1), 0, 0.78, 0.18),
        translated(new BoxGeometry(0.08, 0.54, 0.08), -0.55, 0.27, 0),
        translated(new BoxGeometry(0.08, 0.54, 0.08), 0.55, 0.27, 0),
      ], "street-furniture:bench"),
      material: materials.busStopSeat,
    }];
  }
  if (kind === "planter") {
    return [{
      name: "planted-separator-box",
      geometry: translated(new BoxGeometry(1.6, 0.48, 0.68), 0, 0.24, 0),
      material: materials.oldTownPlanter,
    }, {
      name: "planted-separator-greenery",
      geometry: translated(new BoxGeometry(1.38, 0.18, 0.5), 0, 0.53, 0),
      material: materials.treeCrownDark,
    }];
  }
  return [{
    name: "hydrant-red-body",
    geometry: mergeOwned([
      translated(
        new CylinderGeometry(0.12, 0.14, CONFIG.hydrantHeightM, 10),
        0,
        CONFIG.hydrantHeightM / 2,
        0,
      ),
      translated(new SphereGeometry(0.14, 10, 6), 0, CONFIG.hydrantHeightM, 0),
      translated(new CylinderGeometry(0.07, 0.07, 0.34, 8), 0, 0.52, 0),
    ], "street-furniture:hydrant"),
    material: materials.roadSignRed,
  }];
}

function stableYaw(value: string): number {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash, 31) + value.charCodeAt(index);
  }
  return ((hash % 360) + 360) % 360;
}

function translated<T extends BufferGeometry>(
  geometry: T,
  x: number,
  y: number,
  z: number,
): T {
  geometry.translate(x, y, z);
  return geometry;
}

function mergeOwned(
  geometries: readonly BufferGeometry[],
  name: string,
): BufferGeometry {
  const merged = mergeGeometries([...geometries]);
  for (const geometry of geometries) geometry.dispose();
  if (merged === null) throw new Error("Could not merge " + name);
  merged.name = name;
  merged.computeBoundingBox();
  merged.computeBoundingSphere();
  return merged;
}
